import {
  ModalManager,
  useModal,
} from "@circle-react/components/Layout/ModalManagerProvider";
import { useCircleV4 } from "@circle-react/hooks/useCircleV4";
import type { Event } from "@circle-react/types";
import { Suspense } from "react";

import { lazyWithRetry } from "@/react/helpers/lazyRetry";
import { PageLoader } from "@/react/pages/PageLoader";

import { FullScreenModal } from "../../shared/ModalsV2/FullScreenModal";
import { CompassEventModalSuspenseFallback } from "../shared/CompassEventModalSuspenseFallback";

const EventsV3 = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "EventsV3Modal" */ "@circle-react/components/EventsV3/Modal"
  ).then(module => ({
    default: module.EventsV3,
  })),
);

const CompassEventModal = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "CompassEditEventModal" */ "@circle-react/components/Events/CompassEventModal"
  ).then(module => ({
    default: module.CompassEventModal,
  })),
);

interface EditEventModalProps {
  defaultSection?: string;
  event?: Event;
  locationType?: string;
  onClose?: () => void;
  onSuccess?: (event: Event) => void;
}

const EditEventModalComponent = ({
  event,
  defaultSection,
  locationType,
  onSuccess,
  onClose,
}: EditEventModalProps) => {
  const { visible: isVisible, hide, remove } = useModal();
  const { isCircleV4 } = useCircleV4();

  const closeCompassModal = () => {
    void hide();
    onClose?.();
  };

  if (isCircleV4) {
    return (
      <Suspense
        fallback={
          <CompassEventModalSuspenseFallback
            isOpen={isVisible}
            onClose={closeCompassModal}
          />
        }
      >
        <CompassEventModal
          isOpen={isVisible}
          onClose={closeCompassModal}
          event={event}
          defaultSection={defaultSection}
          onSuccess={onSuccess}
        />
      </Suspense>
    );
  }

  return (
    <FullScreenModal
      isOpen={isVisible}
      shouldShowCloseButton={false}
      contentPortalId="events"
      onClose={remove}
    >
      <Suspense fallback={<PageLoader />}>
        <EventsV3
          isOpen={isVisible}
          onClose={hide}
          event={event}
          locationType={locationType}
          initialSection={defaultSection}
        />
      </Suspense>
    </FullScreenModal>
  );
};

const EditEventModal = ModalManager.create(
  ({
    event,
    defaultSection,
    locationType,
    onSuccess,
    onClose,
  }: EditEventModalProps) => (
    <EditEventModalComponent
      event={event}
      defaultSection={defaultSection}
      locationType={locationType}
      onSuccess={onSuccess}
      onClose={onClose}
    />
  ),
);

export const useEditEventModal = () => useModal(EditEventModal);
